/**
 * Baut aus einem gesprochenen Satz einen Protokoll-Eintrag.
 *
 * Zwei Wege zum selben Ergebnis: zuerst ein echter Claude-Aufruf über den
 * lokalen Server (server/index.mjs), der versteht, was gemeint ist. Schlägt
 * der fehl — kein Netz, Server nicht erreichbar, Übergabe im Keller ohne
 * Empfang — fällt die Funktion automatisch auf die einfache regelbasierte
 * Erkennung zurück. Offline muss immer gehen (siehe AGENTS.md); der Nutzer
 * merkt vom Umschalten nichts außer einer etwas gröberen Erkennung.
 */
import { RAUM_VORSCHLAEGE, type Zustand } from './domain/types';

export interface SatzErgebnis {
	/** Erkannter Raum, falls der Satz einen nennt. */
	raum?: string;
	/** Rest des Satzes als Mangel-Eintrag, falls einer erkennbar ist. */
	mangel?: {
		gegenstand: string;
		beschreibung: string;
		zustand: Zustand;
	};
}

const API_BASIS = import.meta.env.VITE_API_BASIS_URL ?? '';
const API_TIMEOUT_MS = 8000;

/**
 * Versucht, den Satz über den echten Claude-Aufruf zu strukturieren.
 * `null` heißt: nicht verfügbar (Netz, Timeout, Server, Format) — der
 * Aufrufer fällt dann auf die regelbasierte Erkennung zurück, statt einen
 * Fehler nach oben zu werfen.
 */
async function strukturiereUeberApi(satz: string, bekannteRaeume: string[]): Promise<SatzErgebnis | null> {
	const abbruch = new AbortController();
	const timer = setTimeout(() => abbruch.abort(), API_TIMEOUT_MS);

	try {
		const antwort = await fetch(`${API_BASIS}/api/strukturiere`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ satz, bekannteRaeume }),
			signal: abbruch.signal
		});
		if (!antwort.ok) return null;

		const daten: unknown = await antwort.json();
		return istGueltigesErgebnis(daten) ? daten : null;
	} catch {
		return null;
	} finally {
		clearTimeout(timer);
	}
}

function istGueltigesErgebnis(wert: unknown): wert is SatzErgebnis {
	if (typeof wert !== 'object' || wert === null) return false;
	const kandidat = wert as Record<string, unknown>;

	if (kandidat.raum !== undefined && kandidat.raum !== null && typeof kandidat.raum !== 'string') return false;

	if (kandidat.mangel === undefined || kandidat.mangel === null) return true;
	if (typeof kandidat.mangel !== 'object') return false;
	const mangel = kandidat.mangel as Record<string, unknown>;
	const gueltigeZustaende: Zustand[] = ['in_ordnung', 'gebrauchsspuren', 'mangel'];
	return (
		typeof mangel.gegenstand === 'string' &&
		typeof mangel.beschreibung === 'string' &&
		gueltigeZustaende.includes(mangel.zustand as Zustand)
	);
}

/** Bereinigt ein API-Ergebnis in die Form, die die App erwartet (raum: null → undefined). */
function normalisiere(ergebnis: SatzErgebnis): SatzErgebnis {
	return {
		raum: ergebnis.raum ?? undefined,
		mangel: ergebnis.mangel ?? undefined
	};
}

const MANGEL_WOERTER = [
	'fleck',
	'kratzer',
	'loch',
	'riss',
	'defekt',
	'kaputt',
	'locker',
	'fehlt',
	'beschädigt',
	'abgenutzt',
	'schimmel',
	'feucht',
	'undicht',
	'klemmt',
	'wackelt'
];

const IN_ORDNUNG_WOERTER = ['ordnung', 'einwandfrei', 'sauber', 'neuwertig', 'neu', 'gut'];

function findeRaum(satz: string): string | undefined {
	const klein = satz.toLowerCase();
	return RAUM_VORSCHLAEGE.find((raum) => klein.includes(raum.toLowerCase()));
}

function schaetzeZustand(satz: string): Zustand {
	const klein = satz.toLowerCase();
	if (MANGEL_WOERTER.some((wort) => klein.includes(wort))) return 'mangel';
	if (IN_ORDNUNG_WOERTER.some((wort) => klein.includes(wort))) return 'in_ordnung';
	return 'gebrauchsspuren';
}

/**
 * Sehr einfache Heuristik für den Gegenstand: das erste sinntragende Wort vor
 * dem ersten Komma, oder der ganze Satzanfang bis zu drei Wörtern. Der
 * Offline-Fallback — gröber als der Claude-Aufruf, aber ohne Netz das
 * Einzige, was zuverlässig geht.
 */
function schaetzeGegenstand(satz: string): string {
	const vorKomma = satz.split(',')[0]?.trim() ?? satz;
	const woerter = vorKomma.split(/\s+/).filter(Boolean);
	return woerter.slice(0, 3).join(' ') || satz.trim();
}

function strukturiereRegelbasiert(satz: string): SatzErgebnis {
	const raum = findeRaum(satz);

	// Ein reiner Raumwechsel ohne weiteren Inhalt — kein Mangel-Eintrag.
	const nurRaumname = raum && satz.toLowerCase().replace(/[.,]/g, '').trim() === raum.toLowerCase();
	if (nurRaumname) return { raum };

	// Den erkannten Raumnamen am Satzanfang abschneiden ("Küche, Herd
	// verschmutzt" → "Herd verschmutzt"), sonst taucht er als Gegenstand auf.
	let rest = satz;
	if (raum && satz.toLowerCase().startsWith(raum.toLowerCase())) {
		rest = satz.slice(raum.length).replace(/^[\s,.:]+/, '').trim() || satz;
	}

	return {
		raum,
		mangel: {
			gegenstand: schaetzeGegenstand(rest),
			beschreibung: rest,
			zustand: schaetzeZustand(rest)
		}
	};
}

/**
 * Baut aus einem gesprochenen Satz einen Protokoll-Eintrag — Räume, aus
 * denen bereits welche im Protokoll stehen, werden mitgeschickt, damit
 * Claude vorhandene Räume wiedererkennt statt Duplikate mit leicht anderer
 * Schreibweise anzulegen.
 */
export async function strukturiere(satz: string, bekannteRaeume: string[] = []): Promise<SatzErgebnis> {
	const bereinigt = satz.trim();
	if (!bereinigt) return {};

	const ausApi = await strukturiereUeberApi(bereinigt, bekannteRaeume);
	if (ausApi) return normalisiere(ausApi);

	return strukturiereRegelbasiert(bereinigt);
}
